import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Plus, Tags } from 'lucide-react';
import { PRODUCT_CATEGORIES, type Product, type ProductCategory } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { LoadError } from '../components/feedback/load-error.js';
import { PageHeader } from '../components/layout/page-header.js';
import { QuotaLine } from '../components/billing/resource-meter.js';
import { ProductFormDialog } from '../components/products/product-form-dialog.js';
import { ProductImage } from '../components/products/product-image.js';
import { Badge, Button, useConfirm, Input, ListSkeleton, useToast, cn } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { formatCurrency } from '../lib/format.js';
import { useQuota } from '../lib/billing/use-billing.js';
import { useDeleteProduct, useProducts } from '../lib/products/use-products.js';

/**
 * The price list: what the shop sells, and what it sells for.
 *
 * THE SCREEN IS ORGANISED AROUND THE PRICE, because that is the one thing somebody opens it to
 * find. The name and the price are the two things on every row that read at a glance; the code
 * and the category sit under the name, where somebody looking for them will find them and
 * somebody checking a figure will not have to read past them.
 *
 * RETIRING IS THE DEFAULT WAY OUT, AND DELETING IS NOT. A line the shop has stopped selling is
 * turned off — it leaves the everyday list, keeps its record, and comes back with one click — so
 * the row's ordinary actions are Edit and that toggle. Delete is there for the line somebody
 * typed twice, and its confirmation says plainly that documents already sent are untouched,
 * because "delete product" reads to most people like "unpick every quotation that used it".
 */
export function ProductsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();

  const [q, setQ] = useState('');
  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);

  // Every row is a selling price, so a karigar is refused the screen outright — the same refusal
  // `StockPage` makes, and the same one the API makes behind it.
  const allowed = role !== 'tailor';
  const list = useProducts(
    {
      ...(q ? { q } : {}),
      ...(category ? { category } : {}),
      includeRetired,
    },
    { enabled: allowed },
  );
  const remove = useDeleteProduct();
  // Read here, above the access refusal below, because hooks may not sit after a return.
  const productQuota = useQuota('products');

  if (!allowed) {
    return (
      <>
        <PageHeader title={t('products.title')} />
        <EmptyState
          icon={Lock}
          title={t('products.noAccessTitle')}
          description={t('products.noAccessHint')}
        />
      </>
    );
  }

  const onDelete = async (product: Product): Promise<void> => {
    const confirmed = await confirm({
      title: t('products.deleteTitle', { name: product.name }),
      description: t('products.deleteDescription'),
      confirmLabel: t('common.delete'),
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await remove.mutateAsync(product.id);
      toast({ title: t('products.deleted', { name: product.name }), variant: 'success' });
    } catch {
      toast({ title: t('error.unexpected'), variant: 'destructive' });
    }
  };

  const data = list.data;
  const items = data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t('products.title')}
        description={t('products.description')}
        action={
          <Button onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" />
            {t('products.add')}
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        {/* The catalogue allowance, and only once it is close. See `QuotaLine`. */}
        <QuotaLine usage={productQuota} />

        <Input
          type="search"
          value={q}
          placeholder={t('products.searchPlaceholder')}
          aria-label={t('products.searchPlaceholder')}
          onChange={(event) => setQ(event.target.value)}
        />

        <div
          className="flex flex-wrap gap-1.5 overflow-x-auto"
          role="group"
          aria-label={t('products.filterLabel')}
        >
          <Chip active={category === null} onClick={() => setCategory(null)}>
            {t('products.allCategories')}
          </Chip>
          {PRODUCT_CATEGORIES.map((one) => (
            <Chip
              key={one}
              active={category === one}
              onClick={() => setCategory(category === one ? null : one)}
            >
              {t(`products.category.${one}`)}
            </Chip>
          ))}
        </div>

        {/* Only offered when there is something behind it: a shop that has retired nothing does
            not need to be told it can look at nothing. */}
        {data && (data.retiredCount > 0 || includeRetired) ? (
          <label className="flex items-center gap-2 self-start text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={includeRetired}
              onChange={(event) => setIncludeRetired(event.target.checked)}
            />
            {t('products.showRetired', { count: data.retiredCount })}
          </label>
        ) : null}

        {list.isPending ? <ListSkeleton rows={5} /> : null}

        {list.isError ? <LoadError onRetry={() => void list.refetch()} /> : null}

        {data && items.length === 0 ? (
          <EmptyState
            icon={Tags}
            title={q || category ? t('products.noMatchTitle') : t('products.emptyTitle')}
            description={
              q || category ? t('products.noMatchDescription') : t('products.emptyDescription')
            }
            action={<Button onClick={() => setAdding(true)}>{t('products.add')}</Button>}
          />
        ) : null}

        {items.length > 0 ? (
          <>
            <ul className="flex flex-col gap-1.5">
              {items.map((product) => (
                <li key={product.id}>
                  <ProductRow
                    product={product}
                    onEdit={() => setEditing(product)}
                    onDelete={() => void onDelete(product)}
                    deleting={remove.isPending}
                  />
                </li>
              ))}
            </ul>

            {/* `total` rather than `items.length`: the list is capped, and a count that reported
                only what it was sent would understate a catalogue the moment it outgrew a page. */}
            <p className="text-sm text-muted-foreground" role="status">
              {t('products.count', { count: data?.total ?? items.length })}
            </p>
          </>
        ) : null}
      </div>

      <ProductFormDialog open={adding} onOpenChange={setAdding} />

      {editing ? (
        <ProductFormDialog
          open
          onOpenChange={(open) => !open && setEditing(null)}
          product={editing}
          onSaved={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-transparent bg-primary text-primary-foreground'
          : 'border-border hover:bg-accent',
      )}
    >
      {children}
    </button>
  );
}

function ProductRow({
  product,
  onEdit,
  onDelete,
  deleting,
}: {
  product: Product;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-lg border p-3 text-sm',
        product.active ? 'border-border' : 'border-dashed border-border bg-muted/40',
      )}
    >
      <ProductImage mediaId={product.imageMediaId} alt={product.name} />

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{product.name}</span>
          {product.active ? null : <Badge>{t('products.retired')}</Badge>}
        </span>
        <span className="block text-muted-foreground">
          {t(`products.category.${product.category}`)}
          {product.code ? ` · ${product.code}` : ''}
          {product.ownerName ? ` · ${product.ownerName}` : ''}
        </span>
      </span>

      <span className="text-right">
        <span className="block font-semibold tabular-nums">
          {/* An unpriced line says so in words. A dash would read as zero to somebody scanning. */}
          {product.unitPricePaise === null
            ? t('products.noPrice')
            : formatCurrency(product.unitPricePaise)}
        </span>
      </span>

      <span className="flex flex-wrap items-center gap-1.5">
        <Button variant="ghost" size="sm" onClick={onEdit}>
          {t('common.edit')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive"
          disabled={deleting}
          onClick={onDelete}
        >
          {t('common.delete')}
        </Button>
      </span>
    </div>
  );
}

export default ProductsPage;
